import { useEffect, useMemo, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Eyebrow, Tile, Pill, AreaLine, Empty, fmtTime, fmtClock, fmtMins, PageHead } from "@/components/admin/ui";
import { RecordingsCard } from "@/components/admin/RecordingsCard";
import { WhatsappPanel } from "@/components/admin/WhatsappPanel";
import { PaymentsPanel } from "@/components/admin/PaymentsPanel";
import {
  ArrowLeft,
  Wallet,
  Download,
  Copy,
  ExternalLink,
  MessageSquare,
  ListChecks,
  Phone,
  Users,
  Clock,
  LogOut,
  Video,
  BarChart3,
} from "lucide-react";

const FILTERS = [
  { id: "all", label: "Everyone" },
  { id: "live", label: "Still in class" },
  { id: "early", label: "Left early" },
  { id: "late", label: "Joined late" },
  { id: "engaged", label: "Engaged" },
  { id: "silent", label: "Never spoke" },
];

export default function ClassReport() {
  const { roomName } = useParams();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("minutes");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("summary");
  const [openAnswer, setOpenAnswer] = useState(null);

  const copyList = async (values, msg) => {
    if (!values || values.length === 0) {
      toast("No numbers to copy for this answer");
      return;
    }
    const ok = await copyText(values.join("\n"));
    if (ok) toast.success(msg);
    else toast.error("Couldn't copy — long-press to select instead");
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/meetings/${roomName}/report`);
      setRep(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    document.title = "Class report · Udyog Classes";
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomName]);

  const students = useMemo(() => {
    const list = (rep?.students || []).filter((s) => {
      if (q.trim() && !`${s.name} ${s.whatsapp}`.toLowerCase().includes(q.trim().toLowerCase())) return false;
      if (filter === "live") return s.live;
      if (filter === "early") return s.left_early;
      if (filter === "late") return s.late_minutes > 5;
      if (filter === "engaged") return s.messages > 0 || s.poll_answers > 0;
      if (filter === "silent") return s.messages === 0 && s.poll_answers === 0;
      return true;
    });
    const key = { minutes: "minutes", name: "name", joined: "joined_at", messages: "messages" }[sort];
    return [...list].sort((a, b) =>
      key === "name" ? String(a.name).localeCompare(String(b.name)) : (b[key] || 0) > (a[key] || 0) ? 1 : -1
    );
  }, [rep, filter, sort, q]);

  // Headline counts for the summary tab — every one of them is clickable and
  // drops you into the student list already filtered.
  const totals = useMemo(() => {
    const s = rep?.students || [];
    return [
      { id: "all", label: "Joined the class", value: s.length },
      { id: "live", label: "Still in class", value: s.filter((x) => x.live).length },
      { id: "late", label: "Joined late (5 min+)", value: s.filter((x) => x.late_minutes > 5).length },
      { id: "early", label: "Left early", value: s.filter((x) => x.left_early).length },
      {
        id: "engaged",
        label: "Engaged (chatted or answered)",
        value: s.filter((x) => x.messages > 0 || x.poll_answers > 0).length,
      },
      {
        id: "silent",
        label: "Never spoke",
        value: s.filter((x) => x.messages === 0 && x.poll_answers === 0).length,
      },
    ];
  }, [rep]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/meetings/${roomName}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${roomName}-attendance.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyNumbers = () => {
    const nums = (rep?.students || []).map((s) => s.whatsapp).filter(Boolean);
    if (!nums.length) return toast.error("No WhatsApp numbers captured in this class");
    copyText(nums.join(", "));
    toast.success(`${nums.length} numbers copied`);
  };

  if (err) return <Empty icon={Video} text={err} testid="report-error" />;
  if (!rep) return <div className="py-20 text-center text-white/35">Loading class report…</div>;

  const s = rep.summary;
  const m = rep.meeting;

  return (
    <div className="space-y-6" data-testid="class-report">
      <Link to="/admin/classes" className="inline-flex items-center gap-2 text-[11.5px] text-white/40 hover:text-emerald-300">
        <ArrowLeft className="w-3.5 h-3.5" /> All classes
      </Link>

      <PageHead
        eyebrow="Class report"
        title={m.title || roomName}
        sub={`${s.started_at ? fmtTime(s.started_at) : "Not started"} · ${fmtMins(s.duration_minutes)} long`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={copyNumbers}
              className="px-3.5 py-2.5 rounded-full text-[11px] text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] inline-flex items-center gap-1.5"
              data-testid="report-copy-numbers"
            >
              <Phone className="w-3.5 h-3.5" /> Copy numbers
            </button>
            <button
              onClick={exportCsv}
              className="px-3.5 py-2.5 rounded-full text-[11px] text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] inline-flex items-center gap-1.5"
              data-testid="report-export-csv"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button
              onClick={() => {
                const url = m.share_token
                  ? `${window.location.origin}/${m.share_token}`
                  : `${window.location.origin}/join/${roomName}`;
                copyText(url);
                toast.success("Join link copied");
              }}
              className="px-3.5 py-2.5 rounded-full text-[11px] text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] inline-flex items-center gap-1.5"
              data-testid="report-copy-link"
            >
              <Copy className="w-3.5 h-3.5" /> Link
            </button>
            {m.status !== "ended" && (
              <a href={`/meet/${roomName}?host=1`} target="_blank" rel="noreferrer">
                <button
                  className="px-4 py-2.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.14em] bg-emerald-500 hover:bg-emerald-400 text-black inline-flex items-center gap-1.5"
                  data-testid="report-enter-class"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Enter class
                </button>
              </a>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="report-tiles">
        <Tile testid="rt-joined" label="Total joins" value={s.total_joined} hint={`${s.unique_students} unique students`} />
        <Tile testid="rt-peak" label="Peak in class" value={s.peak_concurrent} hint="highest at one moment" />
        <Tile testid="rt-avg" label="Avg. watch" value={fmtMins(s.avg_minutes)} hint={`of ${fmtMins(s.duration_minutes)}`} />
        <Tile
          testid="rt-retention"
          label="Stayed till end"
          value={`${s.retention_pct}%`}
          tone={s.retention_pct >= 70 ? "good" : "warn"}
        />
        <Tile
          testid="rt-engaged"
          label="Engaged"
          value={`${s.engaged_pct}%`}
          hint={`${s.messages} messages`}
          tone={s.engaged_pct >= 30 ? "good" : "default"}
        />
        <Tile testid="rt-wa" label="Numbers captured" value={s.whatsapp_captured} hint={s.live_now ? `${s.live_now} in class now` : "class over"} />
      </div>

      <RecordingsCard roomName={roomName} />

      <Card className="p-5" testid="report-timeline">
        <div className="flex items-center justify-between mb-2">
          <Eyebrow>Who was watching, minute by minute</Eyebrow>
          <span className="text-[10.5px] text-white/35">
            {s.started_at ? fmtClock(s.started_at) : "—"} → {s.ended_at ? fmtClock(s.ended_at) : "now"}
          </span>
        </div>
        {(rep.timeline || []).length === 0 ? (
          <div className="text-[12px] text-white/35">No attendance recorded.</div>
        ) : (
          <>
            <AreaLine points={rep.timeline.map((t) => t.present)} height={150} testid="timeline-chart" />
            <div className="flex items-center justify-between text-[10.5px] text-white/35 mt-1">
              <span>Start</span>
              <span>Peak {s.peak_concurrent}</span>
              <span>End</span>
            </div>
          </>
        )}
      </Card>

      <div className="flex gap-1 p-1 rounded-full bg-black/40 ring-1 ring-white/[0.07] w-fit flex-wrap" data-testid="report-tabs">
        {[
          { id: "summary", label: "Report", icon: BarChart3 },
          { id: "students", label: `Students ${rep.students.length}`, icon: Users },
          { id: "chat", label: `Chat ${rep.chat.length}`, icon: MessageSquare },
          { id: "polls", label: `Polls ${rep.polls.length}`, icon: ListChecks },
          { id: "whatsapp", label: `WhatsApp ${rep.whatsapp.length}`, icon: Phone },
          { id: "fees", label: "Fees & seats", icon: Wallet },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`report-tab-${t.id}`}
            className={`h-9 px-4 rounded-full text-[11.5px] inline-flex items-center gap-1.5 transition-colors ${
              tab === t.id ? "bg-emerald-500 text-black font-semibold" : "text-white/45 hover:text-white hover:bg-white/5"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "summary" && (
        <Card className="p-5" testid="report-summary">
          <Eyebrow>This class in numbers</Eyebrow>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {totals.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setFilter(t.id);
                  setTab("students");
                }}
                className="text-left rounded-2xl bg-white/[0.03] ring-1 ring-white/[0.06] hover:ring-emerald-400/40 p-4 transition-colors"
                data-testid={`total-${t.id}`}
              >
                <div className="font-display text-3xl font-semibold text-white tabular-nums">
                  {t.value}
                </div>
                <div className="text-[11px] text-white/40 mt-1 leading-snug">{t.label}</div>
              </button>
            ))}
            <button
              onClick={() => setTab("whatsapp")}
              className="text-left rounded-2xl bg-emerald-500/[0.07] ring-1 ring-emerald-400/25 hover:ring-emerald-400/60 p-4 transition-colors"
              data-testid="total-whatsapp"
            >
              <div className="font-display text-3xl font-semibold text-emerald-300 tabular-nums">
                {rep.whatsapp.length}
              </div>
              <div className="text-[11px] text-white/40 mt-1 leading-snug">WhatsApp numbers collected</div>
            </button>
            <button
              onClick={() => setTab("chat")}
              className="text-left rounded-2xl bg-white/[0.03] ring-1 ring-white/[0.06] hover:ring-emerald-400/40 p-4 transition-colors"
              data-testid="total-messages"
            >
              <div className="font-display text-3xl font-semibold text-white tabular-nums">
                {rep.chat.length}
              </div>
              <div className="text-[11px] text-white/40 mt-1 leading-snug">Chat messages</div>
            </button>
            <button
              onClick={() => setTab("polls")}
              className="text-left rounded-2xl bg-white/[0.03] ring-1 ring-white/[0.06] hover:ring-emerald-400/40 p-4 transition-colors"
              data-testid="total-answers"
            >
              <div className="font-display text-3xl font-semibold text-white tabular-nums">
                {(rep.polls || []).reduce((n, p) => n + (p.responses || 0), 0)}
              </div>
              <div className="text-[11px] text-white/40 mt-1 leading-snug">Answers to your questions</div>
            </button>
          </div>
          <div className="text-[10.5px] text-white/25 mt-4">
            Tap any number to see exactly who it is.
          </div>
        </Card>
      )}

      {tab === "students" && (
        <Card className="p-5" testid="report-students">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="flex gap-1.5 flex-wrap">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`report-filter-${f.id}`}
                  className={`h-8 px-3 rounded-full text-[11px] transition-colors ${
                    filter === f.id
                      ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30"
                      : "text-white/40 ring-1 ring-white/[0.08] hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search student…"
                className="h-9 px-4 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09] outline-none text-[12px] text-white placeholder-white/30 w-44"
                data-testid="report-student-search"
              />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="h-9 px-3 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09] outline-none text-[11.5px] text-white/70"
                data-testid="report-sort"
              >
                <option value="minutes">Watch time</option>
                <option value="joined">Join time</option>
                <option value="name">Name</option>
                <option value="messages">Messages</option>
              </select>
            </div>
          </div>

          {students.length === 0 ? (
            <div className="text-[12px] text-white/35 py-6 text-center">No students match this filter.</div>
          ) : (
            <div className="overflow-x-auto no-scrollbar">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-[9.5px] uppercase tracking-[0.18em] text-white/30">
                    <th className="pb-3 pr-3 font-normal">Student</th>
                    <th className="pb-3 pr-3 font-normal">In</th>
                    <th className="pb-3 pr-3 font-normal">Out</th>
                    <th className="pb-3 pr-3 font-normal">Watched</th>
                    <th className="pb-3 pr-3 font-normal">Says</th>
                    <th className="pb-3 font-normal">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((st, i) => (
                    <tr
                      key={`${st.name}-${st.joined_at}-${i}`}
                      className="border-t border-white/[0.05] hover:bg-white/[0.02]"
                      data-testid={`report-row-${i}`}
                    >
                      <td className="py-3 pr-3">
                        <div className="text-white/85">{st.name}</div>
                        {st.whatsapp && (
                          <a
                            href={`https://wa.me/91${st.whatsapp}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] text-emerald-400/80 hover:text-emerald-300"
                          >
                            {st.whatsapp}
                          </a>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-white/55 whitespace-nowrap">{fmtClock(st.joined_at)}</td>
                      <td className="py-3 pr-3 text-white/55 whitespace-nowrap">
                        {st.live ? <Pill tone="live">in class</Pill> : fmtClock(st.left_at)}
                      </td>
                      <td className="py-3 pr-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="w-16 h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                            <span
                              className="block h-full bg-emerald-400"
                              style={{ width: `${Math.min(100, st.watch_pct)}%` }}
                            />
                          </span>
                          <span className="text-white/60">{fmtMins(st.minutes)}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-white/55">{st.messages + st.poll_answers || "—"}</td>
                      <td className="py-3">
                        <div className="flex gap-1.5 flex-wrap">
                          {st.late_minutes > 5 && <Pill tone="warn">{st.late_minutes}m late</Pill>}
                          {st.left_early && <Pill tone="bad">left early</Pill>}
                          {st.messages + st.poll_answers > 0 && <Pill tone="good">engaged</Pill>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {tab === "chat" && (
        <Card className="p-5" testid="report-chat">
          <Eyebrow className="mb-4">Chat transcript</Eyebrow>
          {rep.chat.length === 0 ? (
            <div className="text-[12px] text-white/35">Nobody chatted in this class.</div>
          ) : (
            <div className="space-y-3 max-h-[560px] overflow-y-auto no-scrollbar">
              {rep.chat.map((c, i) => (
                <div key={c.id || i} className="flex gap-3" data-testid={`chat-line-${i}`}>
                  <span className="text-[10.5px] text-white/25 w-14 shrink-0 pt-0.5">{fmtClock(c.created_at)}</span>
                  <div className="min-w-0">
                    <span className="text-[11.5px] text-emerald-300/90">{c.user_name}</span>
                    <span className="text-[13px] text-white/75 ml-2 break-words">{c.text}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "polls" && (
        <Card className="p-5 space-y-6" testid="report-polls">
          <Eyebrow>Questions &amp; who answered</Eyebrow>
          {rep.polls.length === 0 ? (
            <div className="text-[12px] text-white/35">No questions were asked in this class.</div>
          ) : (
            rep.polls.map((p, pi) => {
              const total = p.responses || 1;
              return (
                <div key={p.id || pi} className="space-y-3" data-testid={`poll-${pi}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[13.5px] text-white/85">{p.question}</div>
                    <span className="text-[10.5px] text-white/35 whitespace-nowrap">
                      {p.responses} {p.responses === 1 ? "answer" : "answers"}
                    </span>
                  </div>
                  {(p.options || []).map((o, oi) => {
                    const key = o.key ?? oi;
                    const n = p.counts?.[key] ?? 0;
                    const pct = Math.round((n / total) * 100);
                    const who = (p.answers || []).filter((a) => a.choice === key);
                    const open = openAnswer === `${p.id}-${key}`;
                    return (
                      <div key={oi} data-testid={`poll-${pi}-option-${oi}`}>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setOpenAnswer(open ? null : `${p.id}-${key}`)}
                            className="text-[12px] text-white/60 hover:text-white w-40 truncate text-left"
                            data-testid={`poll-${pi}-option-toggle-${oi}`}
                          >
                            {o.label || key}
                          </button>
                          <span className="flex-1 h-2 rounded-full bg-white/[0.07] overflow-hidden">
                            <span className="block h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                          </span>
                          <span className="text-[11.5px] text-white/50 w-14 text-right">
                            {n} · {pct}%
                          </span>
                        </div>
                        {open && (
                          <div
                            className="mt-2.5 ml-1 rounded-2xl bg-black/40 ring-1 ring-white/[0.06] p-3"
                            data-testid={`poll-${pi}-option-list-${oi}`}
                          >
                            {who.length === 0 ? (
                              <div className="text-[11.5px] text-white/30">Nobody picked this.</div>
                            ) : (
                              <>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-400/80">
                                    {who.length} {who.length === 1 ? "person" : "people"}
                                  </div>
                                  <button
                                    onClick={() => {
                                      const nums = who.filter((w) => w.whatsapp).map((w) => `91${w.whatsapp}`);
                                      copyList(
                                        nums,
                                        nums.length === 1 ? "Number copied" : `${nums.length} numbers copied`
                                      );
                                    }}
                                    className="text-[10.5px] text-emerald-300 hover:underline"
                                    data-testid={`poll-${pi}-option-copy-${oi}`}
                                  >
                                    Copy numbers
                                  </button>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-1.5">
                                  {who.map((w, wi) => (
                                    <div
                                      key={wi}
                                      className="flex items-center gap-2 rounded-xl bg-white/[0.03] px-3 py-2"
                                    >
                                      <span className="text-[12px] text-white/80 truncate flex-1">
                                        {w.user_name}
                                      </span>
                                      {w.whatsapp ? (
                                        <>
                                          <a
                                            href={`https://wa.me/91${w.whatsapp}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[11.5px] text-emerald-300 tabular-nums hover:underline"
                                          >
                                            {w.whatsapp}
                                          </a>
                                          <button
                                            onClick={() => copyList([w.whatsapp], "Number copied")}
                                            className="h-6 w-6 grid place-items-center rounded-full text-white/30 hover:text-white hover:bg-white/[0.08]"
                                            data-testid={`poll-copy-one-${w.whatsapp}`}
                                            aria-label="Copy number"
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        </>
                                      ) : (
                                        <span className="text-[11px] text-white/20">no number</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </Card>
      )}

      {tab === "fees" && <PaymentsPanel roomName={roomName} classTitle={m.title || roomName} />}

      {tab === "whatsapp" && (
        <WhatsappPanel
          rows={rep.whatsapp || []}
          polls={rep.polls || []}
          broadcasts={rep.broadcasts || []}
          durationMinutes={rep.class_duration_minutes || s.duration_minutes}
          roomTitle={m.title || roomName}
        />
      )}
    </div>
  );
}
